import React from 'react';

import { Box, Button, Grid, Typography } from '@mui/material';
import { useNavigate } from 'react-router';

import { FullscreenLoader } from '@hrlogics/react-components';

import { appRoutes } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';

import {
  useGetEmployeeDataOnEmployeeSignupByInviteQuery,
  useGetEmploymentDataOnEmployeeSignupByInviteLazyQuery,
  useGetEmploymentDataOnEmployeeSignupByInviteQuery,
  useUserRegistrationByInviteMutation,
} from 'src/api';
import { EmployeeDataEmploymentRecordCard } from 'src/components/employee/EmployeeDataEmploymentRecordCard';
import { PositionDetailsContent } from 'src/components/position';
import { ModalId } from 'src/constants/modals';
import { useNotification } from 'src/hooks/app';
import { useAppAuth, useAuthSignup } from 'src/hooks/auth';
import { useScreenInfo } from 'src/hooks/common';
import { useModal, useModalV2 } from 'src/hooks/shared';
import { CreateDataDisputeOnEmployeeSignupByInviteModal } from 'src/modals/dataDispute';
import { PositionDetailsModalArgs } from 'src/modals/position';

export const ConsumerReviewDataForm: React.FC = () => {
  const navigate = useNavigate();

  const { inviteData, formData, prepareFormData } = useAuthSignup();
  const { setAuthState } = useAppAuth();

  const { isMobile } = useScreenInfo();
  const { openModal } = useModal();
  const { openModal: openModalV2 } = useModalV2();
  const { createApolloContext } = useNotification();

  const [selectedCompanyId, setSelectedCompanyId] = React.useState<Nullable<string>>(null);

  const [signUpUserByInvite, { loading: isSigningUp }] = useUserRegistrationByInviteMutation({
    context: createApolloContext(),
  });

  const { data: employeeData, loading: employeeDataLoading } =
    useGetEmployeeDataOnEmployeeSignupByInviteQuery({
      variables: {
        inviteId: inviteData?.id || '',
      },
      skip: !inviteData?.id,
    });

  const { data: employmentData, loading: employmentDataLoading } =
    useGetEmploymentDataOnEmployeeSignupByInviteQuery({
      variables: {
        inviteId: inviteData?.id || '',
        companyId: selectedCompanyId || '',
      },
      skip: !inviteData?.id || !selectedCompanyId,
    });
  const [getEmploymentData, { loading: employmentDataLazyLoading }] =
    useGetEmploymentDataOnEmployeeSignupByInviteLazyQuery();

  const handleSubmit = React.useCallback(async () => {
    if (!inviteData?.id) return;
    try {
      const { data } = await signUpUserByInvite({
        variables: prepareFormData(inviteData.id, formData),
      });
      const authData = data?.userRegistrationByInvite;
      await setAuthState({
        bearerToken: String(authData?.idToken),
        accessToken: authData?.accessToken ?? undefined,
      });
      navigate(appRoutes.auth.setupMfa);
    } catch (error) {
      console.error(error);
    }
  }, [signUpUserByInvite, formData, inviteData?.id, setAuthState, navigate, prepareFormData]);

  const handleDataDispute = React.useCallback(
    (companyId: string) => {
      if (!inviteData?.id) return;

      openModalV2(CreateDataDisputeOnEmployeeSignupByInviteModal, {
        id: ModalId.CreateDataDisputeOnEmployeeSignupByInvite,
        inviteId: inviteData.id,
        companyId,
      });
    },
    [openModalV2, inviteData?.id],
  );

  const handleChangeView = React.useCallback(
    async (companyId: string) => {
      if (isMobile) {
        const { data: employmentData } = await getEmploymentData({
          variables: { inviteId: inviteData?.id || '', companyId },
        });
        openModal(ModalId.PositionDetails, {
          employmentData: employmentData?.getEmploymentDataOnEmployeeSignupByInvite,
          onDataDispute: handleDataDispute,
        } satisfies PositionDetailsModalArgs);
        return;
      }
      setSelectedCompanyId(companyId);
    },
    [isMobile, getEmploymentData, openModal, inviteData?.id, handleDataDispute],
  );

  if (employeeDataLoading || employmentDataLazyLoading) {
    return <FullscreenLoader />;
  }

  if (selectedCompanyId) {
    return (
      <PositionDetailsContent
        employmentData={employmentData?.getEmploymentDataOnEmployeeSignupByInvite}
        loading={employmentDataLoading}
        onBack={() => setSelectedCompanyId(null)}
        onDataDispute={handleDataDispute}
        isWatermarked
      />
    );
  }

  return (
    <Box>
      <Typography variant="body2" fontSize="24px">
        Review my data
      </Typography>
      <Typography variant="subtitle1" fontSize="16px" mt={1} color="lightslategrey">
        Read following information and confirm if it&apos;s correct
      </Typography>

      <Grid container spacing={2} mt={2}>
        {employeeData?.getEmployeeDataOnEmployeeSignupByInvite.employmentRecords.map(
          employmentRecord => (
            <Grid key={employmentRecord.companyId} size={12}>
              <EmployeeDataEmploymentRecordCard
                key={employmentRecord.companyId}
                employmentRecord={employmentRecord}
                onSeeMore={handleChangeView}
                loading={employmentDataLoading}
                hideEmployerLiveIndicator
              />
            </Grid>
          ),
        )}

        <Grid size={12} textAlign="center">
          <Button
            type="submit"
            color="primary"
            variant="contained"
            onClick={() => handleSubmit()}
            disabled={
              isSigningUp ||
              !employeeData?.getEmployeeDataOnEmployeeSignupByInvite.employmentRecords ||
              employeeData.getEmployeeDataOnEmployeeSignupByInvite.employmentRecords.length === 0
            }
            loading={isSigningUp || employmentDataLoading}
          >
            Go to dashboard
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};
